/**
 * ArrangeControl — the toolbar button and mini-panel for align and distribute.
 *
 * Align: line the selected sections up on an edge or centre line, relative to
 * the whole surface ("Wall" / "Ceiling") or to the selection's own extents.
 * Distribute: spread them out with equal gaps, horizontally or vertically —
 * within the selection (the outermost stay put), or across the surface, either
 * edge to edge or with the same gap at both ends as between the sections.
 *
 * Directions here are as SEEN on screen. The workspace turns them into surface
 * coordinates (the left and right walls are drawn mirrored) and decides whether
 * the result is allowed; nothing here moves anything.
 */

import React from 'react';
import {
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignHorizontalSpaceAround,
  AlignVerticalSpaceAround,
  AlignHorizontalDistributeCenter,
  LucideIcon,
} from 'lucide-react';
import { AlignMode, DistributeSpacing } from '../../calculations/sections';
import { DESIGNER_STRINGS as S } from './strings';

export interface ArrangeSettings {
  target: 'surface' | 'selection';
  spacing: DistributeSpacing;
}

export const DEFAULT_ARRANGE: ArrangeSettings = { target: 'surface', spacing: 'around' };

export interface ArrangeControlProps {
  open: boolean;
  onToggle: () => void;
  /** How many sections are selected. */
  count: number;
  surfaceKind: 'wall' | 'ceiling';
  /** What "the whole" is, when it is not the surface (accessories: their SlatWall). */
  frameLabel?: string;
  settings: ArrangeSettings;
  onSettings: (settings: ArrangeSettings) => void;
  /** `mode` is a screen direction (left = left of the screen, top = top of the screen). */
  onAlign: (mode: AlignMode, target: ArrangeSettings['target']) => void;
  onDistribute: (axis: 'x' | 'y', target: ArrangeSettings['target'], spacing: DistributeSpacing) => void;
}

const AMBER = '#FEB100';

const iconButton = (enabled: boolean, active = false): React.CSSProperties => ({
  width: 30, height: 30, flex: '0 0 auto', padding: 0, borderRadius: 7,
  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
  cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.35,
  background: active ? 'rgba(254,177,0,0.18)' : 'rgba(255,255,255,0.06)',
  border: active ? `2px solid ${AMBER}` : '2px solid rgba(255,255,255,0.25)',
});

const segment = (active: boolean, enabled = true): React.CSSProperties => ({
  flex: '1 1 0', height: 26, padding: '0 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
  color: '#fff', whiteSpace: 'nowrap', cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.35,
  background: active ? 'rgba(254,177,0,0.18)' : 'rgba(255,255,255,0.06)',
  border: active ? `1px solid ${AMBER}` : '1px solid rgba(255,255,255,0.25)',
});

const label: React.CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#a9a9b4' };

const ALIGN: { mode: AlignMode; title: string; Icon: LucideIcon }[] = [
  { mode: 'left', title: S.alignLeft, Icon: AlignStartVertical },
  { mode: 'hcenter', title: S.alignHCenter, Icon: AlignCenterVertical },
  { mode: 'right', title: S.alignRight, Icon: AlignEndVertical },
  { mode: 'top', title: S.alignTop, Icon: AlignStartHorizontal },
  { mode: 'vcenter', title: S.alignVCenter, Icon: AlignCenterHorizontal },
  { mode: 'bottom', title: S.alignBottom, Icon: AlignEndHorizontal },
];

export const ArrangeControl: React.FC<ArrangeControlProps> = ({
  open,
  onToggle,
  count,
  surfaceKind,
  frameLabel,
  settings,
  onSettings,
  onAlign,
  onDistribute,
}) => {
  // "Selection" needs at least two sections to mean anything.
  const canUseSelection = count >= 2;
  const target = canUseSelection ? settings.target : 'surface';
  const spacing = target === 'surface' ? settings.spacing : 'between';
  // Within the selection the two outermost stay put, so it takes three to move anything;
  // edge to edge takes two; with margins even one section moves (it is centred).
  const canDistribute = target === 'selection' ? count >= 3 : spacing === 'between' ? count >= 2 : count >= 1;

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" title={S.arrange} aria-label={S.arrange} aria-expanded={open} onClick={onToggle} style={iconButton(true, open)}>
        <AlignHorizontalDistributeCenter size={16} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={S.arrange}
          style={{
            position: 'absolute', top: 40, right: -44, zIndex: 6, width: 300,
            display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 12,
            background: 'rgba(26, 26, 30, 0.97)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={label}>{S.arrangeRelativeTo}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" style={segment(target === 'surface')} onClick={() => onSettings({ ...settings, target: 'surface' })}>
                {frameLabel ?? (surfaceKind === 'ceiling' ? S.arrangeCeiling : S.arrangeWall)}
              </button>
              <button
                type="button"
                disabled={!canUseSelection}
                title={canUseSelection ? undefined : S.multiHint}
                style={segment(target === 'selection', canUseSelection)}
                onClick={() => onSettings({ ...settings, target: 'selection' })}
              >
                {S.arrangeSelection}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={label}>{S.align}</span>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
              {ALIGN.map(({ mode, title, Icon }) => (
                <button key={mode} type="button" title={title} aria-label={title} style={iconButton(true)} onClick={() => onAlign(mode, target)}>
                  <Icon size={16} />
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={label}>{S.distribute}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button type="button" disabled={!canDistribute} title={S.distributeH} aria-label={S.distributeH} style={iconButton(canDistribute)} onClick={() => onDistribute('x', target, spacing)}>
                <AlignHorizontalSpaceAround size={16} />
              </button>
              <button type="button" disabled={!canDistribute} title={S.distributeV} aria-label={S.distributeV} style={iconButton(canDistribute)} onClick={() => onDistribute('y', target, spacing)}>
                <AlignVerticalSpaceAround size={16} />
              </button>
              {target === 'surface' && (
                <div style={{ display: 'flex', gap: 6, flex: '1 1 auto', marginLeft: 4 }}>
                  <button type="button" title={S.spacingAroundTitle} style={segment(spacing === 'around')} onClick={() => onSettings({ ...settings, spacing: 'around' })}>{S.spacingAround}</button>
                  <button type="button" title={S.spacingBetweenTitle} style={segment(spacing === 'between')} onClick={() => onSettings({ ...settings, spacing: 'between' })}>{S.spacingBetween}</button>
                </div>
              )}
            </div>
          </div>

          <span style={{ fontSize: 10.5, color: '#8f8f9b', lineHeight: 1.35 }}>{S.multiHint}</span>
        </div>
      )}
    </div>
  );
};

export default ArrangeControl;
